import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { GET_SALES_DEALS, UPDATE_SALES_DEAL_STATUS, DELETE_SALES_DEAL } from '../../graphql/sales-deals';
import { GET_AGENTS } from '../../graphql/agents';
import { ListComponent } from '../../components/ListComponent';
import { ListRowComponent } from '../../components/ListRowComponent';
import { PaginationComponent } from '../../components/PaginationComponent';

const SalesDeals = () => {
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [orderAsc, setOrderAsc] = useState(false);
    const [orderField, setOrderField] = useState('dateCreated');
    const [filters] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('ALL');
    const [selectedAgent, setSelectedAgent] = useState('ALL');
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
    const [updatingDealId, setUpdatingDealId] = useState(null);

    const { data: salesDealsData, loading, error, refetch } = useQuery(GET_SALES_DEALS, {
        variables: {
            pageIndex,
            pageSize,
            orderAsc,
            orderField,
            filters: {
                ...filters,
                ...(selectedStatus !== 'ALL' && { status: selectedStatus }),
                ...(selectedAgent !== 'ALL' && { agentId: selectedAgent }),
                ...(searchTerm && { search: searchTerm })
            }
        },
        fetchPolicy: 'network-only' // Force refetch from network, bypass cache
    });

    const { data: agentsData } = useQuery(GET_AGENTS, {
        variables: {
            pageIndex: 0,
            pageSize: 1000,
            orderAsc: true,
            orderField: 'firstName'
        }
    });

    const [updateStatus] = useMutation(UPDATE_SALES_DEAL_STATUS, {
        onCompleted: () => {
            setUpdatingDealId(null);
        },
        onError: (error) => {
            console.error('Error updating status:', error);
            setUpdatingDealId(null);
        },
        refetchQueries: [{ query: GET_SALES_DEALS, variables: {
            pageIndex,
            pageSize,
            orderAsc,
            orderField,
            filters: {
                ...filters,
                ...(selectedStatus !== 'ALL' && { status: selectedStatus }),
                ...(selectedAgent !== 'ALL' && { agentId: selectedAgent }),
                ...(searchTerm && { search: searchTerm })
            }
        }}],
        awaitRefetchQueries: true
    });

    const [deleteSalesDeal] = useMutation(DELETE_SALES_DEAL, {
        onCompleted: () => {
            refetch();
        }
    });

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this sales deal?')) {
            try {
                await deleteSalesDeal({ variables: { id } });
            } catch (error) {
                console.error('Error deleting sales deal:', error);
                alert('Error deleting sales deal. Please try again.');
            }
        }
    };

    const handleDealStatusChange = async (id, newStatus) => {
        console.log('Changing status for deal:', id, 'to:', newStatus);
        setUpdatingDealId(id);
        try {
            const result = await updateStatus({ variables: { id, status: newStatus } });
            console.log('Status update result:', result);
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Error updating status. Please try again.');
            setUpdatingDealId(null);
        }
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        setPageIndex(0);
    };

    const handleStatusFilterChange = (status) => {
        console.log('Status filter changed to:', status);
        setSelectedStatus(status);
        setPageIndex(0);
    };

    // Manually refetch when filters change
    useEffect(() => {
        console.log('=== Filter State Changed - Manually Refetching ===');
        console.log('selectedStatus:', selectedStatus);
        console.log('selectedAgent:', selectedAgent);
        console.log('searchTerm:', searchTerm);
        console.log('Computed filters for GraphQL:', {
            ...filters,
            ...(selectedStatus !== 'ALL' && { status: selectedStatus }),
            ...(selectedAgent !== 'ALL' && { agentId: selectedAgent }),
            ...(searchTerm && { search: searchTerm })
        });

        // Manually trigger refetch to bypass Apollo Client's variable comparison issue
        refetch({
            pageIndex,
            pageSize,
            orderAsc,
            orderField,
            filters: {
                ...filters,
                ...(selectedStatus !== 'ALL' && { status: selectedStatus }),
                ...(selectedAgent !== 'ALL' && { agentId: selectedAgent }),
                ...(searchTerm && { search: searchTerm })
            }
        });
        console.log('============================');
    }, [selectedStatus, selectedAgent, searchTerm, filters, refetch, pageIndex, pageSize, orderAsc, orderField]);

    const handleAgentFilterChange = (agentId) => {
        setSelectedAgent(agentId);
        setPageIndex(0);
    };


    const getStatusBadge = (status) => {
        const badges = {
            PENDING: 'bg-yellow-100 text-yellow-800',
            CONCLUDED: 'bg-blue-100 text-blue-800',
            COMPLETED: 'bg-green-100 text-green-800',
            FAILED: 'bg-red-100 text-red-800'
        };
        return badges[status] || 'bg-gray-100 text-gray-800';
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-ZA', {
            style: 'currency',
            currency: 'ZAR',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-ZA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const renderRow = (deal) => {
        const agentNames = deal.agents?.map(a => `${a.agentId.firstName} ${a.agentId.lastName}`).join(', ') || 'No Agent';
        
        return (
            <ListRowComponent
                key={deal.id}
                isSelected={false}
                onRowClick={() => {}}
            >
                <div className="p-4 hover:bg-gray-50">
                    {/* Main Deal Information */}
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center mb-3">
                        {/* Deal Info */}
                        <div className="md:col-span-2">
                            <div className="flex flex-col">
                                <span className="font-medium text-gray-900">{deal.dealNumber}</span>
                                <span className="text-sm text-gray-600">{deal.streetAddress}</span>
                                <span className="text-xs text-gray-500">{deal.suburb}, {deal.city}</span>
                            </div>
                        </div>

                        {/* Price & Commission */}
                        <div className="text-center">
                            <div className="font-semibold text-green-600">{formatCurrency(deal.sellingPrice)}</div>
                            <div className="text-sm text-gray-600">
                                {deal.totalCommissionPercentage}% comm.
                            </div>
                        </div>

                        {/* Agents */}
                        <div className="text-sm text-gray-700">
                            <div className="font-medium text-gray-500 text-xs mb-1">Agent(s)</div>
                            {agentNames}
                        </div>

                        {/* Property Details & Status */}
                        <div className="flex flex-col space-y-2">
                            <div className="text-sm text-gray-600">
                                {deal.propertyType && (
                                    <div>{deal.propertyType}</div>
                                )}
                                {(deal.bedrooms || deal.bathrooms || deal.garages) && (
                                    <div>
                                        {deal.bedrooms}B {deal.bathrooms}Ba {deal.garages}G
                                    </div>
                                )}
                            </div>
                            {/* Status Badge */}
                            <div className="flex items-center space-x-2">
                                {(deal.status === 'PENDING' || deal.status === 'CONCLUDED') ? (
                                    <select
                                        value={deal.status}
                                        onChange={(e) => {
                                            if (e.target.value !== deal.status) {
                                                handleDealStatusChange(deal.id, e.target.value);
                                            }
                                        }}
                                        disabled={updatingDealId === deal.id}
                                        className={`px-3 py-1 text-xs font-medium rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-offset-1 ${getStatusBadge(deal.status)} ${
                                            updatingDealId === deal.id ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                    >
                                        <option value={deal.status}>{deal.status}</option>
                                        {deal.status === 'PENDING' && (
                                            <>
                                                <option value="CONCLUDED">CONCLUDED</option>
                                                <option value="FAILED">FAILED</option>
                                            </>
                                        )}
                                        {deal.status === 'CONCLUDED' && (
                                            <>
                                                <option value="COMPLETED">COMPLETED</option>
                                                <option value="FAILED">FAILED</option>
                                            </>
                                        )}
                                    </select>
                                ) : (
                                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(deal.status)}`}>
                                        {deal.status}
                                    </span>
                                )}
                                {deal.needsAttention && (
                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                        Needs Attention
                                    </span>
                                )}
                                <span className="text-xs text-gray-500">{formatDate(deal.dateCreated)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons Row */}
                    <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-100">
                        {/* Change Status Button/Dropdown */}
                        <select
                            value=""
                            onChange={(e) => {
                                if (e.target.value) {
                                    handleDealStatusChange(deal.id, e.target.value);
                                }
                            }}
                            disabled={updatingDealId === deal.id}
                            className={`inline-flex items-center px-3 py-1.5 border border-purple-300 text-xs font-medium rounded-md text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 cursor-pointer ${
                                updatingDealId === deal.id ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                            <option value="">{updatingDealId === deal.id ? 'Updating...' : 'Change Status'}</option>
                            {deal.status === 'PENDING' && (
                                <>
                                    <option value="CONCLUDED">→ Concluded</option>
                                    <option value="FAILED">→ Failed</option>
                                </>
                            )}
                            {deal.status === 'CONCLUDED' && (
                                <>
                                    <option value="COMPLETED">→ Completed</option>
                                    <option value="FAILED">→ Failed</option>
                                </>
                            )}
                            {(deal.status === 'COMPLETED' || deal.status === 'FAILED') && (
                                <option value="PENDING">→ Reopen (Pending)</option>
                            )}
                        </select>
                        <Link
                            to={`/sales/deals/${deal.id}`}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View
                        </Link>
                        <Link
                            to={`/sales/deals/${deal.id}/edit`}
                            className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-xs font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                        </Link>
                        <button
                            onClick={() => handleDelete(deal.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                            <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                        </button>
                    </div>
                </div>
            </ListRowComponent>
        );
    };

    const renderGridCard = (deal) => (
        <div key={deal.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-4">
            {/* Header */}
            <div className="flex justify-between items-start mb-3 pb-3 border-b">
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500">{deal.dealNumber}</span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                            deal.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                            deal.status === 'CONCLUDED' ? 'bg-blue-100 text-blue-800' :
                            deal.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                            {deal.status}
                        </span>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mt-1">{deal.streetAddress}</h3>
                    <p className="text-sm text-gray-500">{deal.suburb}, {deal.city}</p>
                </div>
            </div>

            {/* Financial Info */}
            <div className="mb-3 pb-3 border-b">
                <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-600">Selling Price</span>
                    <span className="text-base font-semibold text-gray-900">{formatCurrency(deal.sellingPrice)}</span>
                </div>
                <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Commission</span>
                    <span className="text-sm font-medium text-blue-600">
                        {deal.commissionInputMethod === 'PERCENTAGE'
                            ? `${deal.totalCommissionPercentage}%`
                            : formatCurrency(deal.totalCommissionAmount)}
                    </span>
                </div>
            </div>

            {/* Agents */}
            {deal.agents && deal.agents.length > 0 && (
                <div className="mb-3">
                    <p className="text-xs font-medium text-gray-500 mb-1">Agents</p>
                    <div className="space-y-1">
                        {deal.agents.map((agent, idx) => (
                            <div key={idx} className="flex items-center text-xs">
                                <span className="text-gray-700">
                                    {agent.agentId.firstName} {agent.agentId.lastName}
                                </span>
                                <span className="text-gray-500 ml-auto">{agent.commissionPercentage}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2">
                <Link
                    to={`/sales/deals/${deal.id}`}
                    className="flex-1 px-3 py-1.5 bg-blue-50 text-blue-600 text-sm font-medium rounded hover:bg-blue-100 transition-colors text-center"
                >
                    View
                </Link>
                <Link
                    to={`/sales/deals/${deal.id}/edit`}
                    className="flex-1 px-3 py-1.5 bg-gray-50 text-gray-600 text-sm font-medium rounded hover:bg-gray-100 transition-colors text-center"
                >
                    Edit
                </Link>
                <button
                    onClick={() => handleDelete(deal.id)}
                    className="px-3 py-1.5 bg-red-50 text-red-600 text-sm font-medium rounded hover:bg-red-100 transition-colors"
                >
                    Delete
                </button>
            </div>
        </div>
    );

    if (loading) return (
        <div className="p-6">
            <div className="animate-pulse">
                <div className="h-8 bg-gray-300 rounded w-1/4 mb-4"></div>
                <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="h-20 bg-gray-300 rounded"></div>
                    ))}
                </div>
            </div>
        </div>
    );

    if (error) return (
        <div className="p-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="text-red-800 font-medium">Error loading sales deals</div>
                <div className="text-red-600 text-sm mt-1">{error.message}</div>
            </div>
        </div>
    );

    const deals = salesDealsData?.salesDeals?.items || [];
    const pageInfo = salesDealsData?.salesDeals?.pageInfo;

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Sales Deals</h1>
                        <p className="text-gray-600">Manage your sales pipeline and track deal progress</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* View Toggle */}
                        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-3 py-2 flex items-center transition-colors ${
                                    viewMode === 'list'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                                title="List View"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`px-3 py-2 flex items-center transition-colors ${
                                    viewMode === 'grid'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                                title="Grid View"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                </svg>
                            </button>
                        </div>

                        <Link
                            to="/sales/deals/add"
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2"
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                            </svg>
                            <span>Add New Deal</span>
                        </Link>
                    </div>
                </div>
            </div>

            {/* Quick status pills */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                {[
                    { key: 'ALL', label: 'All' },
                    { key: 'PENDING', label: 'Pending' },
                    { key: 'CONCLUDED', label: 'Active (Concluded)' },
                    { key: 'COMPLETED', label: 'Completed' },
                    { key: 'FAILED', label: 'Failed' }
                ].map((p) => (
                    <button
                        key={p.key}
                        type="button"
                        onClick={() => handleStatusFilterChange(p.key)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                            selectedStatus === p.key
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Search */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <input
                            type="text"
                            placeholder="Deal number, address..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                    </div>

                    {/* Status Filter (kept for parity with the pills above) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select
                            value={selectedStatus}
                            onChange={(e) => handleStatusFilterChange(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                            <option value="ALL">All Status</option>
                            <option value="PENDING">Pending</option>
                            <option value="CONCLUDED">Concluded (Active)</option>
                            <option value="COMPLETED">Completed</option>
                            <option value="FAILED">Failed</option>
                        </select>
                    </div>

                    {/* Agent Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Agent</label>
                        <select
                            value={selectedAgent}
                            onChange={(e) => handleAgentFilterChange(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                            <option value="ALL">All Agents</option>
                            {agentsData?.agents?.items?.map(agent => (
                                <option key={agent.id} value={agent.id}>
                                    {agent.firstName} {agent.lastName}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                                </svg>
                            </div>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Pending</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {deals.filter(d => d.status === 'PENDING').length}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                            </div>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Concluded</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {deals.filter(d => d.status === 'CONCLUDED').length}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                            </div>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Completed</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {deals.filter(d => d.status === 'COMPLETED').length}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"/>
                                </svg>
                            </div>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Total Value</p>
                            <p className="text-lg font-bold text-gray-900">
                                {formatCurrency(deals.reduce((sum, d) => sum + (d.sellingPrice || 0), 0))}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Deals List */}
            {viewMode === 'list' ? (
                <ListComponent>
                    <div className="space-y-2">
                        {deals.map(renderRow)}
                    </div>

                    {deals.length === 0 && (
                        <div className="text-center py-8">
                            <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5a2 2 0 00-2 2v6a2 2 0 002 2h14m-5 10v2a2 2 0 002 2h6a2 2 0 002-2v-2m-7-8H5a2 2 0 00-2 2v6a2 2 0 002 2h14"/>
                            </svg>
                            <p className="text-gray-500 text-lg">No deals found</p>
                            <p className="text-gray-400 text-sm">Create your first sales deal to get started</p>
                        </div>
                    )}
                </ListComponent>
            ) : (
                <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {deals.map(renderGridCard)}
                    </div>

                    {deals.length === 0 && (
                        <div className="text-center py-8 bg-white rounded-lg shadow">
                            <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5a2 2 0 00-2 2v6a2 2 0 002 2h14m-5 10v2a2 2 0 002 2h6a2 2 0 002-2v-2m-7-8H5a2 2 0 00-2 2v6a2 2 0 002 2h14"/>
                            </svg>
                            <p className="text-gray-500 text-lg">No deals found</p>
                            <p className="text-gray-400 text-sm">Create your first sales deal to get started</p>
                        </div>
                    )}
                </div>
            )}

            {/* Pagination */}
            {pageInfo && pageInfo.totalPages > 1 && (
                <div className="mt-6">
                    <PaginationComponent
                        pageInfo={pageInfo}
                        onPageChange={setPageIndex}
                        onPageSizeChange={(size) => {
                            setPageSize(size);
                            setPageIndex(0);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default SalesDeals;